import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function AileTerapisi() {
  const navigate = useNavigate();
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const problems = [
    'Aile içinde sürekli tekrar eden tartışmalar mı yaşıyorsunuz?',
    'Birbirinizi anlamakta zorlanıyor, anlaşılmama hissi mi var?',
    'Ebeveyn–çocuk arasında iletişim kopukluğu mu yaşıyorsunuz?',
    'Büyükanne / büyükbaba ile sınır problemleri mi var?',
    'Aile birliğini korumakta güçlük mü çekiyorsunuz?',
  ];

  const outcomes = [
    { icon: 'ri-chat-heart-line', title: 'Birbirinizi daha doğru anlamak', desc: 'Aile içi iletişim köklü biçimde yeniden yapılanır.' },
    { icon: 'ri-leaf-line', title: 'Sağlıklı iletişim kurmak', desc: 'Tartışma değil, diyalog; kırgınlık değil, çözüm.' },
    { icon: 'ri-home-heart-line', title: 'Aile içi huzuru yeniden oluşturmak', desc: 'Ailenizin hak ettiği huzur ve sıcaklığa kavuşun.' },
  ];

  return (
    <div className="min-h-screen font-sans bg-cream">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=serene%20family%20therapy%20room%20soft%20golden%20afternoon%20light%20flowing%20through%20sheer%20curtains%20comfortable%20neutral%20furniture%20beige%20ivory%20warm%20tones%20minimal%20peaceful%20atmosphere%20healing%20sanctuary&width=1440&height=700&seq=aile-hero-01&orientation=landscape"
              alt="Aile Terapisi"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-warm-dark/70 via-warm-dark/50 to-warm-dark/70" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-xs text-cream-dark/70 hover:text-gold mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Ana Sayfaya Dön
            </button>
            <span className="text-xs font-medium tracking-[0.2em] text-gold uppercase mb-4 block border border-gold/40 inline-block px-4 py-1.5 rounded-full">
              Danışmanlık Alanı
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-cream-dark mb-5 leading-snug">
              Aile Terapisi
            </h1>
            <div className="inline-flex items-center gap-2 bg-gold/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
              <i className="ri-gift-line text-xs" />
              İlk Seans Ücretsiz
            </div>
            <p className="text-cream-dark/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-3">
              Aile içinde her şey yolunda gibi görünse de, aslında içten içe zorlanıyor olabilirsiniz.
            </p>
            <p className="text-cream-dark/70 text-sm leading-relaxed max-w-xl mx-auto mb-8 italic">
              Bazen herkes haklıdır… ama kimse mutlu değildir.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollTo('randevu-cta')}
                className="px-7 py-3.5 bg-gold text-white text-sm font-medium rounded-full hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Randevu Al
              </button>
              <button
                onClick={() => scrollTo('surec')}
                className="px-7 py-3.5 border border-white/30 text-cream-dark text-sm font-medium rounded-full hover:border-gold hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                Süreci Keşfet
              </button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl font-light text-warm-dark mb-5">Birlikte zorlanmak, yalnız kalmak anlamına gelmez</h2>
              <p className="text-warm-mid text-sm leading-relaxed mb-4">
                Sürekli tekrar eden tartışmalar, anlaşılmama hissi, ebeveyn–çocuk arasında yaşanan iletişim kopuklukları zamanla tüm aile bireylerini yıpratabilir.
              </p>
              <p className="text-warm-mid text-sm leading-relaxed mb-6">
                Uzman Aile Danışmanı Ayşegül Sert, aile içindeki iletişimi yeniden yapılandırmaya ve daha sağlıklı bir denge kurmaya destek olur.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['İletişim yeniden kurulur', 'Sağlıklı sınırlar oluşur', 'Duygusal bağ güçlenir', 'Çatışmalar çözülür'].map(b => (
                  <div key={b} className="flex items-center gap-2 bg-cream rounded-lg px-3 py-2.5 text-xs text-warm-mid">
                    <i className="ri-checkbox-circle-fill text-gold" /> {b}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20cozy%20family%20sitting%20together%20in%20therapy%20room%20minimal%20beige%20interior%20professional%20setting%20therapist%20listening%20attentively%20trust%20comfort%20healing%20soft%20afternoon%20light&width=700&height=500&seq=aile-sec1-01&orientation=landscape"
                alt="Aile Terapisi Süreci"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="surec" className="py-16 md:py-20 bg-cream">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center mb-12">
            <h2 className="font-serif text-3xl font-light text-warm-dark mb-3">Bu süreçte neler öğrenebilirsiniz?</h2>
            <p className="text-warm-mid text-sm max-w-xl mx-auto">Danışmanlık süreci boyunca aile olarak birlikte kazanacaklarınız:</p>
          </div>
          <div className="max-w-4xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map((o) => (
              <div key={o.title} className="bg-white rounded-2xl p-7 flex flex-col items-center text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gold/10 mb-4">
                  <i className={`${o.icon} text-gold text-xl`} />
                </div>
                <h3 className="font-serif text-base font-medium text-warm-dark mb-2">{o.title}</h3>
                <p className="text-warm-mid text-xs leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Problems */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2 className="font-serif text-3xl font-light text-warm-dark text-center mb-3">Bu durum size tanıdık geliyor mu?</h2>
            <p className="text-warm-mid text-sm text-center mb-10">Eğer aşağıdakilerden birini yaşıyorsanız, yardım almanın tam zamanı olabilir.</p>
            <div className="flex flex-col gap-3">
              {problems.map((p, i) => (
                <div key={i} className="flex items-start gap-4 bg-cream rounded-xl px-5 py-4 border border-cream-deeper">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gold/10 flex-shrink-0 mt-0.5">
                    <i className="ri-question-line text-gold text-xs" />
                  </span>
                  <p className="text-sm text-warm-mid">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="randevu-cta" className="py-20 bg-warm-dark text-center">
          <div className="max-w-2xl mx-auto px-4 md:px-8">
            <h2 className="font-serif text-3xl md:text-4xl font-light text-cream-dark mb-4">
              Ailenizin hak ettiği huzura kavuşun
            </h2>
            <p className="text-warm-pale/70 text-sm mb-8">İlk görüşme ücretsizdir. Hiçbir yükümlülük olmadan, sadece tanışalım.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/#randevu')}
                className="px-8 py-3.5 bg-gold text-white text-sm font-medium rounded-full hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Randevu Al
              </button>
              <a
                href="https://wa.me/905309042429"
                target="_blank"
                rel="nofollow noreferrer"
                className="px-8 py-3.5 border border-white/20 text-cream-dark text-sm font-medium rounded-full hover:border-gold hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                Bilgi Almak İstiyorum
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
